import { useEffect } from "react";

export default function DeleteConfirmModal({
  open,
  title = "Konfirmasi",
  message,
  tone = "danger", // "danger" | "default"
  confirmText = "Ya, Hapus",
  cancelText = "Batal",
  onConfirm,
  onCancel,
}) {
  const isDanger = tone === "danger";

  // Esc to close
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onCancel?.();
      if (e.key === "Enter") onConfirm?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onCancel}
        aria-label="Tutup"
      />

      {/* panel */}
      <div className="relative w-[420px] max-w-[92vw] rounded-2xl border border-slate-200/80 bg-white shadow-2xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div
              className={[
                "relative mt-1 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl",
                isDanger
                  ? "bg-gradient-to-br from-red-50 to-red-100/80 text-red-600 shadow-sm shadow-red-100"
                  : "bg-gradient-to-br from-slate-50 to-slate-100/80 text-slate-700 shadow-sm shadow-slate-100",
              ].join(" ")}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.3 4.3 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" />
              </svg>
            </div>

            <div className="flex-1 min-w-0 pt-0.5">
              <div className="text-base font-semibold text-slate-900 tracking-tight">
                {title}
              </div>
              <div className="mt-1.5 text-[15px] text-slate-600 whitespace-pre-line leading-relaxed">
                {message}
              </div>
            </div>

            {/* Close Button */}
            <button
              type="button"
              onClick={onCancel}
              className="flex-shrink-0 rounded-lg p-1.5 text-slate-400 transition-all duration-200 hover:text-slate-600 hover:bg-slate-100"
              aria-label="Tutup"
              title="Tutup"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Divider */}
          <div className="my-5 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-[15px] font-medium text-slate-700 shadow-sm transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 active:scale-[0.98]"
            >
              {cancelText}
            </button>

            <button
              type="button"
              onClick={onConfirm}
              className={[
                "rounded-lg px-4 py-2.5 text-[15px] font-semibold text-white shadow-md transition-all duration-200 active:scale-[0.98]",
                isDanger
                  ? "bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-red-200"
                  : "bg-gradient-to-br from-slate-800 to-slate-900 hover:from-slate-900 hover:to-black shadow-slate-300",
              ].join(" ")}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
